/**
 * MCP tool router — maps bridge tool names to their in-app handlers.
 *
 * This is the single execution point for every external MCP invocation.
 * Unknown tool names are rejected here (the relay mirrors the catalog, so
 * a mismatch only happens if the app and relay versions drift).
 */

import type { BridgeToolResult } from './mcpBridgeProtocol';
import { MCP_TOOL_CATALOG, MCP_TOOL_BY_NAME } from './mcpToolCatalog';
import type { McpToolHandler } from './mcpResultUtils';
import { fail } from './mcpResultUtils';
import {
    handleExecuteCommand,
    handleGetState,
    handleListCommands,
    handleRunScript,
    handleSetMode,
    handleSetToolOption,
} from './mcpCommandTools';
import {
    handleControlView,
    handleCreateElement,
    handleDeleteElements,
    handleDocumentAction,
    handleGetElement,
    handleListLayers,
    handleManageLayers,
    handleQueryScene,
    handleScreenshot,
    handleSelectElements,
    handleUpdateElement,
} from './mcpSceneTools';
import {
    handleImportIfc,
    handleListFamilies,
    handlePlaceFamily,
} from './mcpFamilyTools';
import { handleValidateDesign } from './mcpValidateTools';
import { handleListDesignTemplates, handleStartDesign } from './mcpDesignTools';
import { handleAnnotatePlan } from './mcpAnnotateTools';
import { handleGetSystemPrompt } from './mcpSystemPromptTools';

const HANDLERS: Record<string, McpToolHandler> = {
    // command surface
    yap_get_state: handleGetState,
    yap_get_system_prompt: handleGetSystemPrompt,
    yap_list_commands: handleListCommands,
    yap_execute_command: handleExecuteCommand,
    yap_run_script: handleRunScript,
    yap_set_mode: handleSetMode,
    yap_set_tool_option: handleSetToolOption,
    // structural surface
    yap_query_scene: handleQueryScene,
    yap_get_element: handleGetElement,
    yap_create_element: handleCreateElement,
    yap_update_element: handleUpdateElement,
    yap_delete_elements: handleDeleteElements,
    yap_select_elements: handleSelectElements,
    yap_control_view: handleControlView,
    yap_screenshot: handleScreenshot,
    yap_list_layers: handleListLayers,
    yap_manage_layers: handleManageLayers,
    yap_document_action: handleDocumentAction,
    // family library surface (IFC static families)
    yap_import_ifc: handleImportIfc,
    yap_list_families: handleListFamilies,
    yap_place_family: handlePlaceFamily,
    // design-completeness surface
    yap_validate_design: handleValidateDesign,
    yap_list_design_templates: handleListDesignTemplates,
    yap_start_design: handleStartDesign,
    yap_annotate_plan: handleAnnotatePlan,
};

// Boot-time self-check: every catalog entry must have a handler and vice
// versa, so router/catalog drift is loud during development, not silent at
// tool-call time.
for (const spec of MCP_TOOL_CATALOG) {
    if (!HANDLERS[spec.name]) console.warn(`[MCP] Catalog tool '${spec.name}' has no handler.`);
}
for (const name of Object.keys(HANDLERS)) {
    if (!MCP_TOOL_BY_NAME.has(name)) console.warn(`[MCP] Handler '${name}' is not in the published catalog.`);
}

/** Execute one bridge tool call. Never throws; failures return ok:false. */
export async function executeMcpTool(
    tool: string,
    args: Record<string, unknown>,
): Promise<BridgeToolResult> {
    const handler = HANDLERS[tool];
    if (!handler) {
        return fail(`Unknown MCP tool '${tool}'. Available: ${Object.keys(HANDLERS).join(', ')}.`);
    }
    try {
        return await handler(args ?? {});
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error(`[MCP] Tool '${tool}' threw:`, error);
        return fail(`Tool '${tool}' failed: ${message}`);
    }
}
